import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { useGetAllGroups } from "@synthesis/shared-services/auth/group/hooks";
import { Input, Select } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { useUserRolesGGE } from "../../../../hooks";
import type { CreateProjectFormData } from "../create-project-schema";

type RenderFormItemProps = {
  title: string;
  subtitle: string;
  name: "code" | "description" | "name" | "objectName";
  placeholder: string;
  mask?: boolean;
  userGroupName?: string;
};

type ObjectNameFieldsProps = {
  placeholder: string;
  selectGroups: { value: string; label: string }[];
  isLoading: boolean;
  isModeration: boolean;
};

const ObjectNameFields = ({ placeholder, selectGroups, isLoading, isModeration }: ObjectNameFieldsProps) => {
  const methods = useFormContext<CreateProjectFormData>();
  const selectVal = methods.watch("objectNameSelect");
  const inputVal = methods.watch("objectNameInput");

  React.useEffect(() => {
    if (inputVal) {
      methods.setValue("objectNameSelect", null, { shouldValidate: true });
      methods.setValue("objectName", inputVal, { shouldValidate: true });
      return;
    }

    if (selectVal) {
      methods.setValue("objectNameInput", "", { shouldValidate: true });
      methods.setValue("objectName", selectVal, { shouldValidate: true });
      return;
    }

    methods.setValue("objectName", "", { shouldValidate: true });
  }, [inputVal, selectVal, methods]);

  return (
    <div className="flex w-full gap-2">
      <div className={cn(isModeration ? "w-full" : "w-1/2")}>
        <Controller
          control={methods.control}
          name="objectNameSelect"
          render={({ field }) => (
            <Select
              value={field.value ? String(field.value) : ""}
              items={selectGroups}
              onChange={(val: string) => {
                field.onChange(val);
                methods.setValue("objectNameInput", "");
              }}
              placeholder="Выберете организацию"
              isLoading={isLoading}
              size="md"
            />
          )}
        />
      </div>

      {!isModeration && <Input className="w-1/2" name="objectNameInput" placeholder={placeholder} clearable />}
    </div>
  );
};

export const RenderFormItem = ({ title, subtitle, name, placeholder, mask, userGroupName }: RenderFormItemProps) => {
  const roles = useUserRolesGGE();
  const { data: groups = [], isLoading } = useGetAllGroups(undefined, {
    query: { enabled: name === "objectName" },
  });

  const groupData = groups.map(({ name: groupName }) => ({
    value: groupName ?? "",
    label: groupName ?? "",
  }));

  const settingsProps = mask ? { settings: { mask: "*****-**/***-*******" } } : {};

  const selectGroups = roles.МОДЕРИРОВАНИЕ ? [{ value: userGroupName ?? "", label: userGroupName ?? "" }] : groupData;

  return (
    <div>
      <div className="mb-4 flex items-center">
        <h3 className="text-lg font-bold">{title}</h3>
        <span className="text-type-error pl-1">*</span>
      </div>
      <h4 className="paragraph-sm pb-2">{subtitle}</h4>
      {name === "objectName" ? (
        <ObjectNameFields placeholder={placeholder} selectGroups={selectGroups} isLoading={isLoading} isModeration={roles.МОДЕРИРОВАНИЕ} />
      ) : (
        <Input {...settingsProps} name={name} type="text" placeholder={placeholder} />
      )}
    </div>
  );
};
